import * as signalR from '@microsoft/signalr';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class SignalRService {
  private qrHub?: signalR.HubConnection;
  private attendanceHub?: signalR.HubConnection;

  private currentQrCourseStartId?: number;
  private currentAttendanceCourseStartId?: number;
  private currentYoklamaSuresi?: number;

  // Callback'leri merkezi yönetmek için (Dışarıdan gelen dinleyiciler)
  private onAttendanceUpdateCb?: (students: any[]) => void;
  private onNewTokenCb?: (data: any) => void;
  private onQrExpiredCb?: () => void;
  private onAttendanceExpiredCb?: () => void;

  // ================= QR HUB =================

  async connectQrHub(courseStartId: number, payload: any) {

    this.currentQrCourseStartId = courseStartId;

    // Bağlantı zaten varsa koparma, sadece re-subscribe yap
    if (this.qrHub && this.qrHub.state === signalR.HubConnectionState.Connected) {

      await this.subscribeQrHub(courseStartId, payload);
      return;
    }

    if (this.qrHub) await this.qrHub.stop();

    const token = localStorage.getItem('accessToken') || '';
    this.qrHub = new signalR.HubConnectionBuilder()
      .withUrl(environment.baseUrl.split('/api')[0] + '/qrtokenhub', {
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000])
      .build();

    this.registerQrEvents();
    await this.qrHub.start();

    await this.subscribeQrHub(courseStartId, payload);
  }

  private async subscribeQrHub(courseStartId: number, payload: any) {
    if (!this.qrHub) return;
    try {
      await this.qrHub.invoke(
        'SubscribeToCourseStart',
        Number(courseStartId),
        payload.courseId,
        payload.haftalikDersId,
        payload.slotNumber,
        payload.yoklamaSuresi,
        payload.qrUret,
        payload.konumKontrol,
        payload.acilanDersDetayNo,
        payload.akademikPersonelNo,
        payload.acilanDersNo
      );

    } catch (err) {
      console.error('❌ QR Subscribe hatası:', err);
    }
  }

  private registerQrEvents() {
    if (!this.qrHub || (this.qrHub as any)._isRegistered) return;

    this.qrHub.on('ReceiveNewToken', (data) => {

      if (this.onNewTokenCb) this.onNewTokenCb(data);
    });

    this.qrHub.on('QrSessionExpired', () => {
      console.warn('⛔ QR süresi doldu');
      if (this.onQrExpiredCb) this.onQrExpiredCb();
    });

    this.qrHub.onreconnected(async () => {

      if (this.currentQrCourseStartId) {
        // Not: Burada payload lazım ama reconnected durumunda
        // son payload'u saklamak veya basit bir subscribe metodu kullanmak gerekebilir.
      }
    });

    (this.qrHub as any)._isRegistered = true;
  }

  // ================= ATTENDANCE HUB =================

  async connectAttendanceHub(courseStartId: number, yoklamaSuresi: number) {

    this.currentAttendanceCourseStartId = courseStartId;
    this.currentYoklamaSuresi = yoklamaSuresi;

    if (this.attendanceHub && this.attendanceHub.state === signalR.HubConnectionState.Connected) {

      await this.subscribeAttendanceHub(courseStartId, yoklamaSuresi);
      return;
    }

    if (this.attendanceHub) await this.attendanceHub.stop();

    const token = localStorage.getItem('accessToken') || '';
    this.attendanceHub = new signalR.HubConnectionBuilder()
      .withUrl(environment.baseUrl.split('/api')[0] + '/attendancehub', {
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000])
      .build();

    this.registerAttendanceEvents();
    await this.attendanceHub.start();

    await this.subscribeAttendanceHub(courseStartId, yoklamaSuresi);
  }

  private async subscribeAttendanceHub(courseStartId: number, yoklamaSuresi: number) {
    if (!this.attendanceHub) return;
    try {
      await this.attendanceHub.invoke(
        'SubscribeToAttendance',
        Number(courseStartId),
        Number(yoklamaSuresi)
      );

    } catch (err) {
      console.error('❌ Attendance Subscribe hatası:', err);
    }
  }

  private registerAttendanceEvents() {
    if (!this.attendanceHub || (this.attendanceHub as any)._isRegistered) return;

    this.attendanceHub.on('ReceiveNewAttendance', (data: any) => {

      const list = Array.isArray(data) ? (Array.isArray(data[0]) ? data[0] : data) : [];
      if (this.onAttendanceUpdateCb) this.onAttendanceUpdateCb(list);
    });

    this.attendanceHub.on('AttendanceSessionExpired', () => {
      console.warn('⛔ Attendance süresi doldu');
      if (this.onAttendanceExpiredCb) this.onAttendanceExpiredCb();
    });

    this.attendanceHub.onreconnected(async () => {

      if (this.currentAttendanceCourseStartId && this.currentYoklamaSuresi) {
        await this.subscribeAttendanceHub(this.currentAttendanceCourseStartId, this.currentYoklamaSuresi);
      }
    });

    (this.attendanceHub as any)._isRegistered = true;
  }

  // ================= EXTERNAL LISTENERS (Dışarıya Açılanlar) =================

  onNewToken(cb: (data: any) => void) { this.onNewTokenCb = cb; }
  onQrExpired(cb: () => void) { this.onQrExpiredCb = cb; }
  onAttendanceUpdate(cb: (students: any[]) => void) { this.onAttendanceUpdateCb = cb; }
  onAttendanceExpired(cb: () => void) { this.onAttendanceExpiredCb = cb; }

  // ================= ACTIONS =================

  sendStudentUpdate(data: any) {
    this.attendanceHub?.invoke('UpdateStudent', data)
      .then(() => console.log('✅ Student update OK'))
      .catch((err) => console.error('❌ Student update error:', err));
  }

  // ================= CLEANUP =================

  async disconnectAll() {

    try {
      // Önce dinleyicileri kapatıyoruz
      if (this.qrHub) {
        await this.qrHub.stop();
        (this.qrHub as any)._isRegistered = false; // Flag reset
      }
      if (this.attendanceHub) {
        await this.attendanceHub.stop();
        (this.attendanceHub as any)._isRegistered = false; // Flag reset
      }

    } catch (e) {
      console.error('❌ disconnectAll error', e);
    }
  }
}
